import { AuthUser } from "aws-amplify/auth";
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import i18n from "i18next";
import I18nTextUtils from "../../utils/i18n/I18nTextUtils";
import PlaceDataUtils, { PlaceDefinition } from "../../utils/PlaceDataUtils";
import StorageUtils from "../../utils/StorageUtils";
import EmptyImageUtils from "../../utils/image/EmptyImageUtils";
import PlaceCategoryBadgeNoAmplify from "../places/PlaceCategoryBadgeNoAmplify";
import PlacePropertyBadgeNoAmplify from "../places/PlacePropertyBadgeNoAmplify";
import OneStarRatingThumbnail from '../rating/OneStarRatingThumbnail';
import { PlaceMarker } from './PlaceMarker';
import './PlaceMarkerHover.css';

export class PlaceMarkerHoverClass extends PlaceMarker {
    private mainImageUrl: string | null = null;

    constructor(placeDefinition: PlaceDefinition, loggedInUser: AuthUser, handlePlaceClick: (placeDefinition: PlaceDefinition) => void) {
        super(placeDefinition, loggedInUser, handlePlaceClick);
        
        // Set an invisible placeholder icon initially
        this.setIcon(super.createInvisibleIcon());
        this.loadMainImage();
        this.setupClickHandler();
    }

    private async loadMainImage() {
        try {
            const mainImagePath = await StorageUtils.getPlaceMainImagePath(this.placeDefinition.placeId);
            if (mainImagePath) {
                const url = await StorageUtils.getImageUrl(mainImagePath);
                this.mainImageUrl = url;
                this.setIcon(this.createCustomIcon()); // Update icon with new image
            } else {
                this.mainImageUrl = EmptyImageUtils.getPlaceholderImage();
                this.setIcon(this.createCustomIcon()); // Update icon with placeholder
            }
        } catch (error) {
            console.error("Failed to load main image:", error);
            this.mainImageUrl = EmptyImageUtils.getPlaceholderImage();
            this.setIcon(this.createCustomIcon()); // Update icon with placeholder
        }
    }

    private createCustomIcon(): L.DivIcon {
        const categoryIcon = PlaceDataUtils.getPlaceCategoryDetail(this.placeDefinition.placeCategories[0])?.iconPath;
        const averageRating = this.placeDefinition.averageRating || 0;
        const normalizedRating = averageRating / 5;

        const markerHtml = ReactDOMServer.renderToStaticMarkup(
            <div className={"marker-container"}>
                <div className="marker-place-name-badge disablehover">
                    {I18nTextUtils.resolveInternationalizedText(this.placeDefinition.name)}
                </div>
                <div className="marker-content-column-flex">
                    <div className="marker-content-row-flex">
                        <div className="marker-circle">
                            <img
                                src={categoryIcon}
                                alt="category"
                                className="marker-category-icon"
                            />
                            {this.mainImageUrl && (
                                <img
                                    src={this.mainImageUrl}
                                    className="marker-main-image"
                                />
                            )}
                        </div>
                    </div>
                    <div className="marker-pointer disablehover"></div>
                </div>
                <div className="marker-right-wing-flex-column disablehover">
                    <div className="marker-category-badge disablehover">
                        <PlaceCategoryBadgeNoAmplify categoryEnum={this.placeDefinition.placeCategories[0]} />
                    </div>
                    <div className="marker-rating disablehover">
                        <OneStarRatingThumbnail
                            rating={normalizedRating}
                            numberOfRatings={this.placeDefinition.numberOfRatings}
                        />
                    </div>
                </div>
                <div className="marker-left-wing-flex-column disablehover">
                    {[...(this.placeDefinition.placeProperties || [])]
                        .sort((a, b) => {
                            const aLength = i18n.t('place.properties.' + a).length;
                            const bLength = i18n.t('place.properties.' + b).length;
                            return aLength - bLength;
                        })
                        .map((property) => (
                            <div key={property} className="marker-property-badge">
                                <PlacePropertyBadgeNoAmplify
                                    propertyEnum={property}
                                />
                            </div>
                        ))}
                </div>
            </div>
        );

        return L.divIcon({
            className: 'custom-marker',
            html: markerHtml,
            iconSize: [30, 40],
            iconAnchor: [15, 40],
        });
    }

    private setupClickHandler() {
        this.on('click', (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e);
            if (e.originalEvent) {
                e.originalEvent.stopPropagation();
            }
            this.handlePlaceClick(this.placeDefinition);
        });

        // Add right-click handler for "open in new tab" functionality
        this.on('contextmenu', (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e);
            if (e.originalEvent) {
                e.originalEvent.preventDefault();
                e.originalEvent.stopPropagation();
            }
            // Open place URL in new tab
            const placeUrl = `${window.location.origin}/place/${this.placeDefinition.placeId}`;
            window.open(placeUrl, '_blank');
        });
    }
} 